import type { CalendarMeeting, CalendarProvider } from '@ai-meeting/shared'
import { ListIcon } from '@/components/Icons'
import { isBriefEligible } from '@/components/brief/briefVisibility'
import { useUiStrings } from '@/hooks/useUiStrings'
import { formatDateTime, formatTime } from '@/utils/formatDateTime'

export function TeamsMeetingGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="3" y="6" width="12" height="12" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m15 10 6-3v10l-6-3M6 10h6M9 10v5" />
    </svg>
  )
}

export function GoogleMeetGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="2" y="6" width="13" height="12" rx="2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m15 11 7-4v10l-7-4" />
    </svg>
  )
}

/** The one glyph that tells a Teams row from a Meet row at a glance. */
export function CalendarMeetingGlyph({ provider }: { provider: CalendarProvider }) {
  return provider === 'google' ? <GoogleMeetGlyph /> : <TeamsMeetingGlyph />
}

export function CalendarMeetingTime({ meeting }: { meeting: CalendarMeeting }) {
  const { locale } = useUiStrings()
  const start = new Date(meeting.startsAt)
  const end = new Date(meeting.endsAt)

  return (
    <>
      <time dateTime={meeting.startsAt}>{formatDateTime(start, locale)}</time>
      {' – '}
      <time dateTime={meeting.endsAt}>
        {start.toDateString() === end.toDateString()
          ? formatTime(end, locale)
          : formatDateTime(end, locale)}
      </time>
    </>
  )
}

export function CalendarJoinLink({
  meeting,
  provider,
}: {
  meeting: CalendarMeeting
  provider: CalendarProvider
}) {
  const { t } = useUiStrings()
  const label = provider === 'google' ? t.googleCalendarJoin : t.teamsCalendarJoin
  return (
    <a
      href={meeting.joinUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label}: ${meeting.title}`}
      className="btn-primary inline-flex shrink-0 items-center justify-center px-4 py-2 text-xs"
    >
      {label}
    </a>
  )
}

/**
 * Calendar events share the Home list styling, not BrSE recording IDs or routes.
 *
 * Brief is offered on the same terms as the full listing — `briefEligibleIds`
 * is computed from the full provider week — so the preview matches /upcoming.
 */
export default function CalendarMeetingRow({
  meeting,
  provider,
  briefEligibleIds,
  onOpenBrief,
}: {
  meeting: CalendarMeeting
  provider: CalendarProvider
  /** Ids allowed to open Brief, from the full provider week. */
  briefEligibleIds?: ReadonlySet<string>
  /** Given only when this panel offers Brief at all. */
  onOpenBrief?: (meeting: CalendarMeeting) => void
}) {
  const { t } = useUiStrings()
  const eligible = briefEligibleIds ?? new Set<string>()
  const briefAvailable = Boolean(onOpenBrief) && isBriefEligible(meeting, eligible)

  return (
    <article className="grid min-h-[76px] grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3 shadow-sm transition-colors hover:border-[var(--accent)] sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-5 sm:px-5">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-blue-100 text-[var(--accent)]">
        <CalendarMeetingGlyph provider={provider} />
      </div>
      <div className="min-w-0">
        <h3
          className="truncate text-sm font-semibold text-[var(--text-primary)]"
          title={meeting.title}
        >
          {meeting.title}
        </h3>
        <p className="mt-0.5 text-xs text-[var(--text-secondary)]">
          <CalendarMeetingTime meeting={meeting} />
        </p>
        {meeting.organizer && (
          <p
            className="mt-0.5 truncate text-xs text-[var(--text-secondary)]"
            title={meeting.organizer}
          >
            {meeting.organizer}
          </p>
        )}
      </div>
      <div className="col-start-2 flex flex-wrap items-center justify-start gap-2 sm:col-auto sm:justify-end">
        {briefAvailable && (
          <button
            type="button"
            onClick={() => onOpenBrief?.(meeting)}
            title={`${t.briefView}: ${meeting.title}`}
            aria-label={`${t.briefButton}: ${meeting.title}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-md border border-[var(--border-color)] bg-[var(--bg-primary)] px-2.5 py-1 text-[11px] font-medium text-[var(--accent)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            <ListIcon className="h-3 w-3" />
            <span>{t.briefButton}</span>
          </button>
        )}
        <CalendarJoinLink meeting={meeting} provider={provider} />
      </div>
    </article>
  )
}
